import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import AuthModal from '../components/AuthModal';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import { useBrandSettings } from '../context/BrandSettingsContext';
import { Shield, CheckCircle2, Crown, LogOut, User, AlertCircle, Menu, X, Download, Share2, TrendingUp } from 'lucide-react';
import { mockCategories } from '../mock/mockData';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import BrandLogo from '../components/BrandLogo';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '../components/ui/sheet';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tagline, tagline_style, getFontWeightValue, logos } = useBrandSettings();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMessage, setAuthMessage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [claimsVerifiedCount, setClaimsVerifiedCount] = useState(0);

  const [prefillClaim, setPrefillClaim] = useState('');

  useEffect(() => {
    // Handle auth success/error from OAuth callback
    if (location.state?.authSuccess && location.state?.user) {
      setUser(location.state.user);
      setAuthMessage({ type: 'success', text: `Welcome, ${location.state.user.name}!` });
      // Clear the location state
      window.history.replaceState({}, document.title);
      // Auto-dismiss message after 3 seconds
      setTimeout(() => setAuthMessage(null), 3000);
    } else if (location.state?.authError) {
      setAuthMessage({ type: 'error', text: location.state.authError });
      window.history.replaceState({}, document.title);
      setTimeout(() => setAuthMessage(null), 5000);
    }
    
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    // Check for claim parameter from browser extension
    const urlParams = new URLSearchParams(window.location.search);
    const claimFromExtension = urlParams.get('claim');
    if (claimFromExtension) {
      // Set the claim directly to pass to SearchBar
      setPrefillClaim(decodeURIComponent(claimFromExtension));
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Load claims verified count for social proof
    loadClaimsCount();
  }, [location.state]);

  const loadClaimsCount = async () => {
    try {
      const data = await apiService.getClaimsVerifiedCount();
      setClaimsVerifiedCount(data.count || 0);
    } catch (error) {
      console.error('Error loading claims count:', error);
    }
  };

  const handleSearch = async (searchData) => {
    setIsLoading(true);
    try {
      // Call real backend API
      const result = await apiService.factCheck(
        searchData.content,
        searchData.type,
        searchData.type === 'image' ? searchData.content : null,
        searchData.type === 'url' ? searchData.content : null
      );
      
      // For free users, store in sessionStorage
      const storedUser = localStorage.getItem('user');
      const userData = storedUser ? JSON.parse(storedUser) : null;
      const isPremium = userData && ['pro', 'premium', 'enterprise'].includes(userData.subscription_tier);
      const isAdmin = userData && userData.is_admin;
      
      if (!isPremium && !isAdmin) {
        // Store in sessionStorage for free users
        const sessionChecks = JSON.parse(sessionStorage.getItem('session_fact_checks') || '[]');
        sessionChecks.unshift(result);
        sessionStorage.setItem('session_fact_checks', JSON.stringify(sessionChecks.slice(0, 20))); // Keep last 20
      }
      
      navigate('/results', { state: { result } });
    } catch (error) {
      console.error('Verification error:', error);
      let errorMessage = error.message || 'An error occurred';
      
      // Handle specific error codes
      if (error.message?.includes('520') || error.message?.includes('timeout')) {
        errorMessage = 'The request timed out. If uploading an image, please try a smaller file.';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      navigate('/results', { state: { error: errorMessage } });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
      localStorage.removeItem('user');
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const handleAuthSuccess = (newUser) => {
    setUser(newUser);
  };

  // Check for premium subscription - use snake_case as returned by backend
  const isPremium = user && (user.subscription_tier === 'pro' || user.subscription_tier === 'premium' || user.subscription_tier === 'enterprise');
  const isAdmin = user && user.is_admin;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
          </div>

          {/* Desktop Nav - Hidden on Mobile */}
          <nav className="hidden md:flex items-center gap-4">
            <a href="/about" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors">
              About
            </a>
            <a 
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors cursor-pointer"
            >
              How It Works
            </a>
            <a href="/history" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors">
              History
            </a>
            <a href="/directory" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors">
              Directory
            </a>
            <a href="/share" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors flex items-center gap-1">
              <Download className="w-4 h-4" />
              Install
            </a>
            <button 
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'KaiCheckIt - AI Content Verification',
                    text: `Check out KaiCheckIt - ${tagline} 🛡️`,
                    url: 'https://app.kaicheckit.com'
                  });
                } else {
                  navigator.clipboard.writeText(`Check out KaiCheckIt - ${tagline} 🛡️ https://app.kaicheckit.com`);
                  alert('Link copied to clipboard!');
                }
              }}
              className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors flex items-center gap-1"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-medium">
                    Admin
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/admin/users')}>
                    Manage Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/sources')}>
                    Manage Sources
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/factcheckers')}>
                    Manage Fact-Checkers
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/directory')}>
                    Manage Directory
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/complaints')}>
                    Complaints
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/prompts')}>
                    Manage Prompts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/brand-settings')}>
                    Brand Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/payment-settings')}>
                    Payment Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/maintenance')}>
                    Maintenance Banner
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <ThemeToggle />
            <Button
              onClick={() => navigate('/pricing')}
              size="sm"
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
            >
              <Crown className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="max-w-[100px] truncate">{user.name}</span>
                    {isPremium && <Crown className="w-3 h-3 text-purple-600" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user.name}</span>
                      <span className="text-xs text-slate-500 font-normal">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Badge variant={isPremium ? "default" : "secondary"} className="w-full justify-center">
                      {isPremium ? "Pro" : "Free Plan"}
                    </Badge>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  {!isPremium && (
                    <DropdownMenuItem onClick={() => navigate('/pricing')}>
                      <Crown className="w-4 h-4 mr-2 text-purple-600" />
                      Upgrade to Pro
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => setShowAuthModal(true)}
                variant="outline"
                size="sm"
              >
                Sign In
              </Button>
            )}
          </nav>

          {/* Mobile Hamburger Menu */}
          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button variant="ghost" size="sm" className="p-1">
                <User className="w-5 h-5 text-slate-600" />
              </Button>
            ) : (
              <Button
                onClick={() => setShowAuthModal(true)}
                variant="outline"
                size="sm"
                className="text-xs px-2"
              >
                Sign In
              </Button>
            )}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-white dark:bg-slate-900 flex flex-col">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
                    <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    KaiCheckIt
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-2 mt-6 overflow-y-auto flex-1 pb-4">
                  {user && (
                    <>
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg mb-2">
                        <p className="font-medium text-slate-800 dark:text-slate-100">{user.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
                        <Badge variant={isPremium ? "default" : "secondary"} className="mt-2">
                          {isPremium ? "Premium" : "Free Plan"}
                        </Badge>
                      </div>
                      <SheetClose asChild>
                        <a href="/profile" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium">
                          <User className="w-4 h-4" />
                          My Profile
                        </a>
                      </SheetClose>
                    </>
                  )}
                  <SheetClose asChild>
                    <a href="/about" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium">
                      About
                    </a>
                  </SheetClose>
                  <a 
                    href="#how-it-works"
                    onClick={(e) => {
                      e.preventDefault();
                      setMobileMenuOpen(false);
                      // Small delay to allow sheet to close first
                      setTimeout(() => {
                        document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                      }, 150);
                    }}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium cursor-pointer"
                  >
                    How It Works
                  </a>
                  <SheetClose asChild>
                    <a href="/history" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium">
                      History
                    </a>
                  </SheetClose>
                  <SheetClose asChild>
                    <a href="/directory" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium">
                      Verified Directory
                    </a>
                  </SheetClose>
                  <SheetClose asChild>
                    <a href="/share" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium">
                      <Download className="w-4 h-4" />
                      Install App
                    </a>
                  </SheetClose>
                  <SheetClose asChild>
                    <button 
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: 'KaiCheckIt - AI Content Verification',
                            text: `Check out KaiCheckIt - ${tagline} 🛡️`,
                            url: 'https://app.kaicheckit.com'
                          });
                        } else {
                          navigator.clipboard.writeText(`Check out KaiCheckIt - ${tagline} 🛡️ https://app.kaicheckit.com`);
                          alert('Link copied to clipboard!');
                        }
                      }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-medium w-full text-left"
                    >
                      <Share2 className="w-4 h-4" />
                      Share App
                    </button>
                  </SheetClose>
                  {isAdmin && (
                    <>
                      <div className="border-t border-slate-200 dark:border-slate-700 my-2" />
                      <p className="text-xs text-slate-500 dark:text-slate-400 px-3">Admin</p>
                      <SheetClose asChild>
                        <a href="/admin/users" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Manage Users
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/sources" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Manage Sources
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/factcheckers" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Manage Fact-Checkers
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/directory" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Manage Directory
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/complaints" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Complaints
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/prompts" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Manage Prompts
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/brand-settings" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Brand Settings
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/payment-settings" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Payment Settings
                        </a>
                      </SheetClose>
                      <SheetClose asChild>
                        <a href="/admin/maintenance" className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200">
                          Maintenance Banner
                        </a>
                      </SheetClose>
                    </>
                  )}
                  <div className="border-t border-slate-200 dark:border-slate-700 my-2" />
                  <SheetClose asChild>
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      {isPremium ? 'Manage Plan' : 'Upgrade to Pro'}
                    </Button>
                  </SheetClose>
                  {user && (
                    <SheetClose asChild>
                      <Button
                        onClick={handleLogout}
                        variant="outline"
                        className="w-full mt-2"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </Button>
                    </SheetClose>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Auth Message Toast */}
      {authMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-md px-4">
          <Alert 
            className={authMessage.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/40 border-green-200 dark:border-green-700' 
              : 'bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-700'
            }
          >
            {authMessage.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : (
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            )}
            <AlertDescription className={authMessage.type === 'success' 
              ? 'text-green-800 dark:text-green-200' 
              : 'text-red-800 dark:text-red-200'
            }>
              {authMessage.text}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Hero Section - Optimized for Virality */}
      <main className="container mx-auto px-6">
        <div className="flex flex-col items-center justify-center pt-16 pb-12">
          
          {/* Search Box FIRST - Above Everything */}
          <div className="w-full max-w-2xl mb-10">
            <SearchBar onSearch={handleSearch} isLoading={isLoading} initialClaim={prefillClaim} />
          </div>

          {/* Brand Tagline - Clean & Viral */}
          <div className="text-center mb-8 max-w-3xl">
            <h1 className="text-5xl sm:text-6xl font-bold mb-2 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 dark:from-blue-400 dark:via-blue-500 dark:to-blue-600 bg-clip-text text-transparent leading-tight">
              KaiCheckIt
            </h1>
            <p 
              className="text-lg sm:text-xl md:text-2xl"
              style={{ 
                color: `var(--tagline-color, ${tagline_style?.color_light || '#334155'})`,
                fontWeight: getFontWeightValue()
              }}
            >
              <style>{`
                .dark p[style*="--tagline-color"] { color: ${tagline_style?.color_dark || '#cbd5e1'} !important; }
              `}</style>
              <span className="light-tagline dark:hidden" style={{ color: tagline_style?.color_light || '#334155' }}>{tagline}</span>
              <span className="dark-tagline hidden dark:inline" style={{ color: tagline_style?.color_dark || '#cbd5e1' }}>{tagline}</span>
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-8">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span>Real-time</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span>AI-Powered</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span>Multi-Source</span>
            </div>
          </div>

          {/* Claims Verified Counter - Desktop: above search, Mobile: below search */}
          <div className="hidden sm:block mb-6">
            {claimsVerifiedCount > 0 && (
              <div className="flex items-center justify-center gap-2 text-slate-600 dark:text-slate-400">
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-500" />
                <span className="text-lg font-semibold">
                  {claimsVerifiedCount.toLocaleString()}
                </span>
                <span className="text-base">claims verified</span>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Claims Counter - appears below search bar area */}
        <div className="sm:hidden text-center pb-6">
          {claimsVerifiedCount > 0 && (
            <div className="flex items-center justify-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span>{claimsVerifiedCount.toLocaleString()} claims verified</span>
            </div>
          )}
        </div>

        {/* How It Works */}
        <div id="how-it-works" className="py-16 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12 text-slate-800 dark:text-slate-100">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-700 text-center transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BrandLogo variant="plain" size="lg" data-testid="brand-logo-standalone" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-800 dark:text-slate-100">1. Submit the Suspicious Post</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Paste the text, upload a screenshot, or share any content you want to verify before believing or sharing.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-700 text-center transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-800 dark:text-slate-100">2. Kai Analyzes</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Kai checks real-time news, trusted sources, and Fact-Checker databases to evaluate the content.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-200 dark:border-slate-700 text-center transform hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-slate-800 dark:text-slate-100">3. Kai Reports</h3>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Get a clear verdict (Verified, Inconclusive, or Debunked) with confidence score and source citations.
              </p>
            </div>
          </div>

          {/* Pro CTA for Free Users */}
          {!isPremium && (
            <div className="mt-12 max-w-3xl mx-auto">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white text-center shadow-2xl">
                <Crown className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Go Unlimited with Pro</h3>
                <p className="mb-6 text-purple-100">
                  Free tier: 5 verifications/month. Pro: Unlimited verifications, persistent history, and export reports.
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-white text-purple-600 hover:bg-slate-100 font-semibold px-8 py-6 text-lg"
                >
                  View Pricing
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md mt-16">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BrandLogo variant="plain" data-testid="brand-logo-footer" />
              <span className="text-slate-600 dark:text-slate-400">© 2025 Kaiventive Holdings</span>
              <span className="text-xs text-slate-400 dark:text-slate-500">v2.3</span>
            </div>
            <div className="flex flex-wrap gap-4 sm:gap-6 justify-center">
              <a href="/transparency" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Transparency</a>
              <a href="/privacy" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Privacy</a>
              <a href="/cookies" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Cookies</a>
              <a href="/terms" className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;